// src/pages/Judgement.jsx - Redesigned to match reference layout exactly
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Navbar from '../components/Navbar';
import RightSidebar from '../components/RightSidebar';
import ApiService from '../services/apiService';
import GoogleTranslate from "../components/GoogleTranslate";
import Header from '../components/Header';

const JudgementApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [judgmentData, setJudgmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('issue');
  const [activeNotesTab, setActiveNotesTab] = useState('advocates');
  // Refs for scroll targets
  const issueRef = useRef(null);
  const lawPointsRef = useRef(null);
  const headnotesRef = useRef(null);
  const heldRef = useRef(null);
  const factsRef = useRef(null);
  const contentionsRef = useRef(null);
  const dispositionRef = useRef(null);
  const judgmentOrderRef = useRef(null);
  const [marks, setMarks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  useEffect(() => {
    document.body.style.paddingTop = '0';

    return () => {
      document.body.style.paddingTop = '';
    };
  }, []);

  useEffect(() => {
    if (id) {
      fetchJudgmentDetails(id);

    } else {
      setError('No judgment ID provided');
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (judgmentData) {
      scrollToSection(issueRef, 'issue');
      const allRefs = [issueRef, lawPointsRef, factsRef, contentionsRef, headnotesRef, judgmentOrderRef]; // add all your refs here
      const allMarks = allRefs.reduce((acc, ref) => {
        if (ref.current) {
          const marks = ref.current.querySelectorAll("mark");
          acc.push(...marks);
        }
        return acc;
      }, []);

      setMarks(allMarks);
      setCurrentIndex(-1);
    }

  }, [judgmentData]);

  const scrollToNextMark = () => {
    if (marks.length === 0) return;

    const nextIndex = (currentIndex + 1) % marks.length;
    marks[nextIndex].scrollIntoView({ behavior: "smooth", block: "center" });
    setCurrentIndex(nextIndex);
  };
  // Add this useEffect in your Judgement component

  // In Judgement.jsx - Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            // Trigger previous judgement
            const prevBtn = document.querySelector('[data-action="previous"]');
            if (prevBtn && !prevBtn.disabled) {
              prevBtn.click();
            }
            break;
          case 'ArrowRight':
            event.preventDefault();
            // Trigger next judgement
            const nextBtn = document.querySelector('[data-action="next"]');
            if (nextBtn && !nextBtn.disabled) {
              nextBtn.click();
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const fetchJudgmentDetails = async (keycode) => {
    try {
      setIsLoading(true);
      setError('');

      const response = await ApiService.getJudgementDetailsById(keycode);

      if (response && response.id) {
        setJudgmentData(response);
      } else {
        setError('Judgment not found or invalid response format');
      }

    } catch (error) {
      setError(error.message || 'Failed to load judgment details');
    } finally {
      setIsLoading(false);
    }
  };

  // Add this useEffect after your existing useEffects

  const scrollToSection = (sectionRef, tabName) => {
    setActiveTab(tabName);
    if (sectionRef.current) {
      const offset = 80;
      const elementPosition = sectionRef.current.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';

    try {
      const dateStr = dateString.toString();
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const handleBackToResults = () => {
    navigate('/results');
  };

  if (isLoading) {
    return (
      <div className="gojuris-layout">

        <div className="gojuris-main">




          <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
            <div className="text-center">
              <i className="bx bx-loader bx-spin" style={{ fontSize: '2rem', color: '#E91E63' }}></i>
              <p className="mt-2">Loading judgment details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !judgmentData) {
    return (
      <div className="gojuris-layout">

        <div className="gojuris-main">


          <div className="container mt-5">
            <div className="alert alert-danger">
              <i className="bx bx-error-circle me-2"></i>
              {error || 'Judgment not found'}
            </div>
            <button className="btn btn-primary" onClick={handleBackToResults}>
              <i className="bx bx-arrow-back me-1"></i>
              Back to Results
            </button>
          </div>
        </div>

      </div>
    );
  }

  const tabs = [

    { id: 'headnotes', label: 'HEADNOTES', ref: headnotesRef },
    { id: 'held', label: 'HELD', ref: heldRef },
    { id: 'disposition', label: 'DISPOSITION', ref: dispositionRef }
  ];

  return (
    <div className="judgment-layout">
      <Header />
      <div className="judgment-page-wrapper">



        {/* Main Content */}
        <div className="judgment-main-container">
          <div className="judgment-card">
            {/* Citation & Court Header */}
            <div className="judgment-header-card">
              <div className="citation-line">{judgmentData.mainCitation}</div>
              <div className="court-line">IN THE {judgmentData.court?.toUpperCase()}</div>

              {judgmentData.fullequivicit && (
                <div className="equiv-citations">
                  <strong>Equivalent Citations:</strong> {judgmentData.fullequivicit}
                </div>
              )}

              {judgmentData.judges && (
                <div className="judges-line">
                  <strong>Before:</strong> {judgmentData.judges}
                </div>
              )}

              <div className="parties-box">
                <div className="appellant-line">{judgmentData.appellant}</div>
                <div className="vs-line">vs.</div>
                <div className="respondent-line">{judgmentData.respondent}</div>
              </div>

              <div className="case-details-box">
                {judgmentData.caseNo && (
                  <div className="case-detail-item">
                    <strong>Case No:</strong> {judgmentData.caseNo}
                  </div>
                )}
                <div className="case-detail-item">
                  <strong>Date of Decision:</strong> {formatDate(judgmentData.date)}
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="judgment-content-sections">
              {/* Headnotes */}
              {judgmentData.newHeadnote && (
                <div ref={headnotesRef} className="content-section-card">
                  <div className="section-header-modern">
                    <i className="bx bx-file-find"></i>
                    <h2>Headnotes</h2>
                  </div>
                  <div className="section-content-modern">
                    <div
                      className="headnote-text"
                      dangerouslySetInnerHTML={{ __html: judgmentData.newHeadnote }}
                    />

                    {judgmentData.held && (
                      <div ref={heldRef} className="held-box">
                        <strong className="held-label">Held:</strong>
                        <span dangerouslySetInnerHTML={{ __html: judgmentData.held }} />
                      </div>
                    )}
                  </div>
                </div>
              )}



              {/* Disposition */}
              {judgmentData.disposition && (
                <div ref={dispositionRef} className="content-section-card">
                  <div className="section-header-modern">
                    <i className="bx bx-check-circle"></i>
                    <h2>Disposition</h2>
                  </div>
                  <div
                    className="section-content-modern"
                    dangerouslySetInnerHTML={{ __html: judgmentData.disposition }}
                  />
                </div>
              )}

              {/* Case Notes & Summaries */}
              {judgmentData.headnote && judgmentData.headnote.length > 5 && (
                <div className="content-section-card">
                  <div className="section-header-modern">
                    <i className="bx bx-notepad"></i>
                    <h2>Case Notes & Summaries</h2>
                  </div>
                  <div
                    className="section-content-modern"
                    dangerouslySetInnerHTML={{ __html: judgmentData.headnote }}
                  />
                </div>
              )}

              {/* Tabbed Content: Advocates, Acts, Cases */}
              <div className="tabbed-content-card">
                <div className="tabbed-header">
                  <button
                    className={`tabbed-btn ${activeNotesTab === 'advocates' ? 'active' : ''}`}
                    onClick={() => setActiveNotesTab('advocates')}
                  >
                    <i className="bx bx-user-voice"></i>
                    Advocates
                  </button>
                  <button
                    className={`tabbed-btn ${activeNotesTab === 'acts' ? 'active' : ''}`}
                    onClick={() => setActiveNotesTab('acts')}
                  >
                    <i className="bx bx-book"></i>
                    Acts Referred
                  </button>
                  <button
                    className={`tabbed-btn ${activeNotesTab === 'cases' ? 'active' : ''}`}
                    onClick={() => setActiveNotesTab('cases')}
                  >
                    <i className="bx bx-file"></i>
                    Cases Cited
                  </button>
                </div>

                <div className="tabbed-body">
                  {activeNotesTab === 'advocates' && (
                    <div className="tabbed-content-text">
                      {judgmentData.advocates || 'No advocate information available'}
                    </div>
                  )}
                  {activeNotesTab === 'acts' && (
                    <div
                      className="tabbed-content-text"
                      dangerouslySetInnerHTML={{ __html: judgmentData.actReferred || 'No acts referred' }}
                    />
                  )}
                  {activeNotesTab === 'cases' && (
                    <div
                      className="tabbed-content-text"
                      dangerouslySetInnerHTML={{ __html: judgmentData.casesReferred || 'No cases cited' }}
                    />
                  )}
                </div>
              </div>

              {/* Judgment Order */}
              {judgmentData.judgement && (
                <div ref={judgmentOrderRef} className="content-section-card judgment-order-section">
                  <div className="section-header-modern">
                    <i className="bx bx-file-blank"></i>
                    <h2>Judgment Order</h2>
                  </div>
                  <div
                    className="section-content-modern judgment-order-text"
                    dangerouslySetInnerHTML={{
                      __html: judgmentData.judgement.toString()
                        .replaceAll("<a ", "<a target='_blank' ")
                        .replaceAll(" href=\"#", " href=\"/judgementlink/")
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .judgment-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .judgment-page-wrapper {
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .judgment-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 40px 20px 50px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          position: relative;
        }

        .hero-content-judgment {
          max-width: 1000px;
          margin: 0 auto;
          position: relative;
        }

        .back-to-results-btn {
          position: absolute;
          top: 0;
          left: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-to-results-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateX(-4px);
        }

        .back-to-results-btn i {
          font-size: 18px;
        }

        .judgment-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .judgment-badge i {
          font-size: 16px;
        }

        .judgment-hero-title {
          font-size: 1.8rem;
          font-weight: 800;
          margin: 0 0 16px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.3px;
          line-height: 1.2;
        }

        .judgment-meta-hero {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .meta-item-hero {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          font-weight: 500;
        }

        .meta-item-hero i {
          font-size: 18px;
        }

        .meta-divider-hero {
          width: 1px;
          height: 20px;
          background: rgba(255, 255, 255, 0.3);
        }

        .translate-widget-hero {
          display: inline-flex;
          justify-content: center;
        }

        /* ============================================
           STICKY NAVIGATION TABS
        ============================================ */
        .judgment-tabs-sticky {
          position: sticky;
          top: 140px;
          background: white;
          border-bottom: 2px solid #f0f0f0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          z-index: 100;
          margin-top: -10px;
        }

        .tabs-container-judgment {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .tabs-scroll-wrapper {
          display: flex;
          gap: 4px;
          overflow-x: auto;
          scrollbar-width: thin;
          padding: 12px 0;
        }

        .tabs-scroll-wrapper::-webkit-scrollbar {
          height: 4px;
        }

        .tabs-scroll-wrapper::-webkit-scrollbar-thumb {
          background: #E91E63;
          border-radius: 4px;
        }

        .tab-btn-judgment {
          flex-shrink: 0;
          padding: 10px 18px;
          background: white;
          border: 2px solid #e8e8e8;
          color: #666;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .tab-btn-judgment:hover {
          border-color: #E91E63;
          color: #E91E63;
          background: #fff9f0;
        }

        .tab-btn-judgment.active {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border-color: transparent;
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.3);
        }

        .hits-btn {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .hits-btn i {
          font-size: 16px;
        }

        /* ============================================
           MAIN CONTAINER
        ============================================ */
        .judgment-main-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px 40px;
        }

        .judgment-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }

        /* ============================================
           CITATION HEADER
        ============================================ */
        .judgment-header-card {
          padding: 30px 40px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.03));
          border-bottom: 3px solid #f0f0f0;
          text-align: center;
        }

        .citation-line {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin-bottom: 10px;
        }

        .court-line {
          font-size: 20px;
          font-weight: 800;
          color: #E91E63;
          margin-bottom: 12px;
          text-transform: uppercase;
        }

        .equiv-citations {
          font-size: 14px;
          color: #666;
          margin-bottom: 10px;
        }

        .judges-line {
          font-size: 14px;
          color: #666;
          margin-bottom: 16px;
        }

        .parties-box {
          margin: 20px 0;
          padding: 20px;
          background: white;
          border-radius: 12px;
          border: 2px solid #f0f0f0;
        }

        .appellant-line,
        .respondent-line {
          font-size: 20px;
          font-weight: 700;
          color: #333;
          margin: 8px 0;
        }

        .vs-line {
          font-size: 16px;
          font-weight: 600;
          color: #999;
          margin: 6px 0;
        }

        .case-details-box {
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
          margin-top: 16px;
        }

        .case-detail-item {
          font-size: 14px;
          color: #666;
        }

        .case-detail-item strong {
          color: #333;
          font-weight: 600;
        }

        /* ============================================
           CONTENT SECTIONS
        ============================================ */
        .judgment-content-sections {
          padding: 0;
        }

        .content-section-card {
          padding: 30px 40px;
          border-bottom: 1px solid #f0f0f0;
        }

        .content-section-card:last-child {
          border-bottom: none;
        }

        .section-header-modern {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #f0f0f0;
        }

        .section-header-modern i {
          font-size: 24px;
          color: #E91E63;
        }

        .section-header-modern h2 {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin: 0;
        }

        .section-content-modern {
          font-size: 15px;
          line-height: 1.8;
          color: #555;
          text-align: justify;
        }

        .section-content-modern mark {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.2), rgba(233, 30, 99, 0.1));
          color: #E91E63;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
        }

        .law-points-content {
          padding-left: 20px;
          color: #28a745;
          font-weight: 500;
        }

        .headnote-text {
          margin-bottom: 16px;
          font-weight: 500;
        }

        .held-box,
        .facts-box {
          margin-top: 16px;
          padding: 16px;
          background: #f8f9fa;
          border-left: 4px solid #E91E63;
          border-radius: 8px;
        }

        .held-label,
        .facts-label {
          color: #E91E63;
          font-weight: 700;
          margin-right: 8px;
        }

        /* ============================================
           TABBED CONTENT
        ============================================ */
        .tabbed-content-card {
          margin: 0;
          border-top: 1px solid #f0f0f0;
          overflow: hidden;
        }

        .tabbed-header {
          display: flex;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.03));
          border-bottom: 2px solid #f0f0f0;
        }

        .tabbed-btn {
          flex: 1;
          padding: 16px 20px;
          background: transparent;
          border: none;
          border-right: 1px solid #f0f0f0;
          color: #666;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .tabbed-btn:last-child {
          border-right: none;
        }

        .tabbed-btn:hover {
          background: rgba(245, 166, 35, 0.1);
          color: #E91E63;
        }

        .tabbed-btn.active {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
        }

        .tabbed-btn i {
          font-size: 18px;
        }

        .tabbed-body {
          padding: 30px 40px;
        }

        .tabbed-content-text {
          font-size: 14px;
          line-height: 1.7;
          color: #555;
        }

        /* ============================================
           JUDGMENT ORDER
        ============================================ */
        .judgment-order-section {
          background: #fafafa;
        }

        .judgment-order-text {
          font-family: 'Calibri', sans-serif;
          font-size: 15pt;
        }

        .judgment-order-text a {
          color: #E91E63;
          text-decoration: underline;
          transition: color 0.2s ease;
        }

        .judgment-order-text a:hover {
          color: #c41849;
        }

        /* ============================================
           PRINT STYLES
        ============================================ */
        @media print {
          .judgment-hero,
          .judgment-tabs-sticky,
          .back-to-results-btn,
          .translate-widget-hero {
            display: none !important;
          }

          .judgment-page-wrapper {
            padding-top: 0;
          }

          .judgment-card {
            box-shadow: none;
            border-radius: 0;
          }

          .section-content-modern {
            font-size: 12pt;
          }
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .judgment-page-wrapper {
            padding-top: 130px;
          }

          .judgment-hero {
            padding: 30px 15px 40px;
          }

          .back-to-results-btn {
            position: static;
            margin-bottom: 16px;
          }

          .judgment-hero-title {
            font-size: 1.4rem;
          }

          .judgment-tabs-sticky {
            top: 130px;
          }

          .tabs-container-judgment {
            padding: 0 15px;
          }

          .tab-btn-judgment {
            padding: 8px 14px;
            font-size: 12px;
          }

          .judgment-main-container {
            padding: 20px 15px 30px;
          }

          .judgment-header-card {
            padding: 24px 20px;
          }

          .citation-line {
            font-size: 16px;
          }

          .court-line {
            font-size: 18px;
          }

          .appellant-line,
          .respondent-line {
            font-size: 18px;
          }

          .content-section-card {
            padding: 24px 20px;
          }

          .section-header-modern h2 {
            font-size: 16px;
          }

          .section-content-modern {
            font-size: 14px;
          }

          .tabbed-header {
            flex-direction: column;
          }

          .tabbed-btn {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
          }

          .tabbed-btn:last-child {
            border-bottom: none;
          }

          .tabbed-body {
            padding: 20px;
          }

          .case-details-box {
            flex-direction: column;
            gap: 8px;
          }
        }

        @media (max-width: 480px) {
          .judgment-hero-title {
            font-size: 1.2rem;
          }

          .judgment-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .citation-line {
            font-size: 14px;
          }

          .court-line {
            font-size: 16px;
          }

          .parties-box {
            padding: 16px;
          }

          .appellant-line,
          .respondent-line {
            font-size: 16px;
          }

          .section-header-modern i {
            font-size: 20px;
          }

          .section-header-modern h2 {
            font-size: 15px;
          }

          .section-content-modern {
            font-size: 13px;
          }
        }
           /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default JudgementApp;